import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  ArrowLeft,
  Pencil,
  Play,
  Pause,
  Trash2,
  Zap,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  SkipForward,
  Loader2,
} from "lucide-react"
import { toast } from "sonner"
import {
  useWorkflow,
  useWorkflowExecutions,
  useWorkflowExecutionDetail,
  useActivateWorkflow,
  useDeactivateWorkflow,
  useDeleteWorkflow,
} from "@/hooks/use-workflows"
import type { WorkflowExecution } from "@/lib/types"

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  completed: { label: "Zakończone", color: "#22c55e", icon: CheckCircle2 },
  failed: { label: "Błąd", color: "#ef4444", icon: XCircle },
  running: { label: "W toku", color: "#3b82f6", icon: Loader2 },
  pending: { label: "Oczekuje", color: "#eab308", icon: Clock },
  skipped: { label: "Pominięte", color: "#6b7280", icon: SkipForward },
}

const TRIGGER_EVENT_LABELS: Record<string, string> = {
  DealCreated: "Nowy deal",
  DealPhaseChanged: "Zmiana fazy deala",
  DealWon: "Deal wygrany",
  DealLost: "Deal przegrany",
  TaskCreated: "Nowe zadanie",
  TaskCompleted: "Zadanie ukończone",
  CompanyCreated: "Nowa firma",
  ContactCreated: "Nowy kontakt",
  ServiceTicketCreated: "Nowe zgłoszenie",
  ServiceTicketPhaseChanged: "Zmiana fazy zgłoszenia",
  ServiceTicketResolved: "Zgłoszenie rozwiązane",
  InvoiceIssued: "Faktura wystawiona",
  InvoicePaid: "Faktura opłacona",
  QuoteAccepted: "Oferta zaakceptowana",
  QuoteRejected: "Oferta odrzucona",
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  create_task: "Utwórz zadanie",
  notify_user: "Powiadom",
  create_activity: "Dodaj aktywność",
  update_field: "Aktualizuj pole",
  change_owner: "Zmień właściciela",
  send_email: "Wyślij email",
  send_sms: "Wyślij SMS",
  send_webhook: "Webhook",
  wait: "Czekaj",
}

export function WorkflowDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<"overview" | "executions">("overview")

  const { data, isLoading } = useWorkflow(id ?? "")
  const deleteMutation = useDeleteWorkflow()
  const activateMutation = useActivateWorkflow()
  const deactivateMutation = useDeactivateWorkflow()

  const workflow = data?.data

  const handleDelete = () => {
    if (!id || !workflow) return
    if (!confirm(`Usunąć workflow "${workflow.name}"?`)) return
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success("Workflow usunięty")
        navigate("/automations")
      },
    })
  }

  const handleToggle = async () => {
    if (!id || !workflow) return
    try {
      if (workflow.is_active) {
        await deactivateMutation.mutateAsync(id)
        toast.success("Workflow dezaktywowany")
      } else {
        await activateMutation.mutateAsync(id)
        toast.success("Workflow aktywowany")
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Błąd")
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 rounded-lg animate-pulse bg-muted"
          />
        ))}
      </div>
    )
  }

  if (!workflow) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Workflow nie znaleziony</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/automations")}
            className="p-2 rounded-lg hover:opacity-80 text-muted-foreground"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="page-title text-xl">
                {workflow.name}
              </h1>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${workflow.is_active ? "bg-green-500/10 text-green-500" : "bg-muted text-muted-foreground"}`}
              >
                {workflow.is_active ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
            {workflow.description && (
              <p className="text-sm mt-0.5 text-muted-foreground">
                {workflow.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggle}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${workflow.is_active ? "text-red-500" : "text-green-500"}`}
          >
            {workflow.is_active ? <Pause size={16} /> : <Play size={16} />}
            {workflow.is_active ? "Dezaktywuj" : "Aktywuj"}
          </button>
          <button
            onClick={() => navigate(`/automations/${id}/edit`)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border text-foreground"
          >
            <Pencil size={16} />
            Edytuj
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border text-red-500"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { key: "overview" as const, label: "Przegląd" },
          { key: "executions" as const, label: "Historia wykonań" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium relative ${activeTab === tab.key ? "text-primary" : "text-muted-foreground"}`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        ))}
      </div>

      {activeTab === "overview" ? (
        <OverviewTab workflow={workflow} />
      ) : (
        <ExecutionsTab workflowId={workflow.id} />
      )}
    </div>
  )
}

function OverviewTab({ workflow }: { workflow: NonNullable<ReturnType<typeof useWorkflow>["data"]>["data"] }) {
  return (
    <div className="grid grid-cols-3 gap-6">
      {/* Left: Trigger + Steps */}
      <div className="col-span-2 space-y-4">
        {/* Trigger */}
        <div className="rounded-lg border p-4 bg-card">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={16} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Wyzwalacz
            </h3>
          </div>
          <p className="text-sm text-foreground">
            {TRIGGER_EVENT_LABELS[workflow.trigger_event ?? ""] ?? workflow.trigger_event}
          </p>
          {workflow.conditions.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs font-medium mb-1 text-muted-foreground">
                Warunki:
              </p>
              {workflow.conditions.map((c, i) => (
                <p key={i} className="text-xs text-muted-foreground">
                  {c.field} {c.operator} {c.value}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Steps */}
        {workflow.steps.map((step) => (
          <div key={step.id}>
            <div className="flex justify-center">
              <div className="w-0.5 h-4 bg-border" />
            </div>
            <div className="rounded-lg border p-4 bg-card">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  #{step.step_number}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {ACTION_TYPE_LABELS[step.action_type] ?? step.action_type}
                </span>
              </div>
              {step.action_config && Object.keys(step.action_config).length > 0 && (
                <div className="mt-2 space-y-1">
                  {Object.entries(step.action_config).map(([key, val]) => (
                    <p key={key} className="text-xs text-muted-foreground">
                      <span>{key}:</span> {String(val)}
                    </p>
                  ))}
                </div>
              )}
              {step.delay_seconds > 0 && (
                <p className="text-xs mt-2 flex items-center gap-1 text-muted-foreground">
                  <Clock size={12} />
                  Opóźnienie: {step.delay_seconds}s
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Right sidebar */}
      <div className="space-y-4">
        <div className="rounded-lg border p-4 space-y-3 bg-card">
          <h3 className="text-sm font-semibold text-foreground">
            Informacje
          </h3>
          <InfoRow label="Typ wyzwalacza" value={workflow.trigger_type} />
          <InfoRow label="Kroków" value={String(workflow.steps_count)} />
          <InfoRow
            label="Utworzony"
            value={new Date(workflow.created_at).toLocaleDateString("pl-PL")}
          />
          <InfoRow
            label="Zmodyfikowany"
            value={new Date(workflow.updated_at).toLocaleDateString("pl-PL")}
          />
          {workflow.created_by && (
            <InfoRow
              label="Autor"
              value={`${workflow.created_by.first_name} ${workflow.created_by.last_name}`}
            />
          )}
        </div>

        {workflow.recent_executions.length > 0 && (
          <div className="rounded-lg border p-4 bg-card">
            <h3 className="text-sm font-semibold mb-3 text-foreground">
              Ostatnie wykonania
            </h3>
            <div className="space-y-2">
              {workflow.recent_executions.slice(0, 5).map((exec) => {
                const cfg = STATUS_CONFIG[exec.status] ?? STATUS_CONFIG.pending
                const Icon = cfg.icon
                return (
                  <div key={exec.id} className="flex items-center gap-2">
                    <Icon size={14} style={{ color: cfg.color }} />
                    <span className="text-xs" style={{ color: cfg.color }}>
                      {cfg.label}
                    </span>
                    <span className="text-xs flex-1 text-right text-muted-foreground">
                      {new Date(exec.created_at).toLocaleString("pl-PL")}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-xs text-muted-foreground">
        {label}
      </span>
      <span className="text-xs font-medium text-foreground">
        {value}
      </span>
    </div>
  )
}

function ExecutionsTab({ workflowId }: { workflowId: string }) {
  const { data, isLoading } = useWorkflowExecutions(workflowId)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const executions = data?.data ?? []

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-14 rounded-lg animate-pulse bg-muted"
          />
        ))}
      </div>
    )
  }

  if (executions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 rounded-lg border border-dashed">
        <AlertCircle size={40} className="text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">
          Brak wykonań. Workflow uruchomi się automatycznie po zdarzeniu wyzwalającym.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {executions.map((exec) => (
        <ExecutionRow
          key={exec.id}
          execution={exec}
          workflowId={workflowId}
          isExpanded={expandedId === exec.id}
          onToggle={() => setExpandedId(expandedId === exec.id ? null : exec.id)}
        />
      ))}
    </div>
  )
}

function ExecutionRow({
  execution,
  workflowId,
  isExpanded,
  onToggle,
}: {
  execution: WorkflowExecution
  workflowId: string
  isExpanded: boolean
  onToggle: () => void
}) {
  const { data: detail } = useWorkflowExecutionDetail(
    workflowId,
    isExpanded ? execution.id : ""
  )
  const cfg = STATUS_CONFIG[execution.status] ?? STATUS_CONFIG.pending
  const Icon = cfg.icon

  return (
    <div className="rounded-lg border bg-card">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={onToggle}
      >
        <Icon size={16} style={{ color: cfg.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: cfg.color }}>
              {cfg.label}
            </span>
            <span className="text-xs text-muted-foreground">
              {execution.completed_step_count}/{execution.total_step_count} kroków
            </span>
            {execution.entity_type && (
              <span className="text-xs text-muted-foreground">
                {execution.entity_type}
              </span>
            )}
          </div>
          {execution.error_message && (
            <p className="text-xs mt-0.5 truncate text-red-500">
              {execution.error_message}
            </p>
          )}
        </div>
        <span className="text-xs shrink-0 text-muted-foreground">
          {new Date(execution.created_at).toLocaleString("pl-PL")}
        </span>
        {isExpanded ? (
          <ChevronDown size={16} className="text-muted-foreground" />
        ) : (
          <ChevronRight size={16} className="text-muted-foreground" />
        )}
      </div>

      {isExpanded && detail?.data && (
        <div className="px-4 pb-4 pt-1 border-t space-y-2">
          {detail.data.steps.map((step) => {
            const stepCfg = STATUS_CONFIG[step.status] ?? STATUS_CONFIG.pending
            const StepIcon = stepCfg.icon
            return (
              <div
                key={step.id}
                className="flex items-start gap-2 py-2 border-b last:border-b-0"
              >
                <StepIcon size={14} className="mt-0.5 shrink-0" style={{ color: stepCfg.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground">
                      #{step.step_number}
                    </span>
                    <span className="text-xs font-medium text-foreground">
                      {ACTION_TYPE_LABELS[step.action_type] ?? step.action_type}
                    </span>
                    <span className="text-xs" style={{ color: stepCfg.color }}>
                      {stepCfg.label}
                    </span>
                    {step.duration_ms != null && (
                      <span className="text-xs text-muted-foreground">
                        {step.duration_ms}ms
                      </span>
                    )}
                  </div>
                  {step.error_message && (
                    <p className="text-xs mt-0.5 text-red-500">
                      {step.error_message}
                    </p>
                  )}
                  {step.result_data && Object.keys(step.result_data).length > 0 && (
                    <pre className="text-xs mt-1 p-2 rounded overflow-auto max-h-24 bg-muted text-muted-foreground">
                      {JSON.stringify(step.result_data, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
